import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { urlJoin } from 'url-join-ts';
import { ErrorResult, ErrorType } from '../../../../../../Common/data/error.result';
import { AppSettingsService } from '../../../services/appsettings/app-settings.service';
import { ComponentCommunicationService } from '../../../services/componentcommunication/component-communication.service';
import { ConfigService } from '../../../services/config/config.service';
import { FeatureService } from '../../../services/feature/feature.service';
import { RoutingService } from '../../../services/routing-service/routing.service';
import { StoreRouteService } from '../../../services/storeroute/store.route.service';
import { UserService } from '../../../services/user/user.service';
import { MessageBoxType } from '../../dialog/message-box-content/message-box-content.component';
import { DialogService } from '../../dialog/service/dialog.service';
import { ListCrosswordGameTypes } from '../../list-crossword-games/list-crossword-games.component';

export interface LoginPassportUrls {
  spotify: string;
}

export interface LoginForm {
  email: FormControl<string>;
  password: FormControl<string>;
}

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
  loginForm: FormGroup<LoginForm>;
  submitButtonValid = true;
  loggedIn = true;
  redirectUrl: string = null;
  loginUrls: LoginPassportUrls;

  constructor(
    private userService: UserService,
    private dialogService: DialogService,
    private router: Router,
    private translateService: TranslateService,
    private storeRouteService: StoreRouteService,
    private componentCommunicationService: ComponentCommunicationService,
    private appSettingsService: AppSettingsService,
    private configService: ConfigService,
    private featureService: FeatureService,
  ) {
    this.createForm();
    this.loggedIn = this.userService.isLoggedIn();
    const appSettings = this.appSettingsService.appSettings;
    const routes = this.configService.getRoutes();
    this.loginUrls = {
      spotify: urlJoin(appSettings.apiurl, routes.authentication, 'spotify'),
    };
  }

  async ngOnInit() {
    this.redirectUrl = RoutingService.login.getParams();

    if (this.loggedIn) {
      await this.navigateToStoredOrDefaultUrl();
    }
  }

  async onSubmit() {
    if (!this.componentCommunicationService.allowCookies.value) {
      this.dialogService.showMessageBox('', this.translateService.instant('messages.allow-cookies-before-continue'), MessageBoxType.Ok);
      return;
    }
    if (this.loginForm.valid) {
      this.dialogService.wrapInProgress(async () => {
        try {
          const value = this.loginForm.value;
          await this.userService.login(value.email, value.password);

          if (this.redirectUrl != null) {
            this.router.navigateByUrl(decodeURIComponent(this.redirectUrl));
          } else {
            await this.navigateToStoredOrDefaultUrl();
          }
        } catch (error) {
          const errorResult: ErrorResult = error.error;
          if (errorResult?.type === ErrorType.WRONG_EMAIL) {
            this.loginForm.controls.email.setErrors({ wrongEmail: true });
          } else if (errorResult?.type === ErrorType.WRONG_PASSWORD) {
            this.loginForm.controls.password.setErrors({ wrongPassword: true });
          } else {
            throw error;
          }
        }
      }, this.translateService.instant('messages.signing-in'));
    }
  }

  isSpotifyLoginEnabled() {
    return this.featureService.isFeatureEnabled('spotifyLogin');
  }

  private async navigateToStoredOrDefaultUrl() {
    const storedRoute = await this.storeRouteService.getStoredRoute();
    if (LoginComponent.shouldNavigateToStoredRoute(storedRoute)) {
      this.router.navigateByUrl(storedRoute);
    } else {
      await RoutingService.listCrosswordGames.navigateTo(ListCrosswordGameTypes.Ongoing);
    }
  }

  private static shouldNavigateToStoredRoute(storedRoute: string) {
    if (!storedRoute) {
      return false;
    }
    if (storedRoute == '/') {
      return false;
    }
    return storedRoute.indexOf('login') < 0;
  }

  private createForm() {
    this.loginForm = new FormGroup({
      email: new FormControl('', [Validators.required, Validators.email]),
      password: new FormControl('', [Validators.required, Validators.minLength(6)]),
    });
  }
}
